import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { digitalOcean } from "@/integrations/digitalocean/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Archive as ArchiveIcon, RotateCcw, Trash2 } from "lucide-react";
import { getDefaultCoverImage } from "@/utils/defaultCoverImage";

export default function Archive() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    loadUser();
  }, []);

  const loadUser = async () => {
    if (!session) {
      navigate("/Auth");
      return;
    }
    const { data: profile } = await digitalOcean
      .from("profiles")
      .select("*")
      .eq("id", session.user.id)
      .single();
    setCurrentUser(profile);
  };

  const { data: archivedTracks, isLoading } = useQuery({
    queryKey: ["archived-tracks", currentUser?.id],
    queryFn: async () => {
      const { data, error } = await digitalOcean
        .from("tracks")
        .select("*")
        .eq("artist_id", currentUser.id)
        .eq("is_active", false)
        .order("updated_at", { ascending: false });
      if (error) throw error;
      return data;
    },
    enabled: !!currentUser,
  });

  const restoreMutation = useMutation({
    mutationFn: async (trackId) => {
      const { error } = await digitalOcean
        .from("tracks")
        .update({ is_active: true, updated_at: new Date().toISOString() })
        .eq("id", trackId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["archived-tracks"]);
      toast.success("Track restored successfully!");
    },
    onError: () => {
      toast.error("Failed to restore track");
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (trackId) => {
      const { error } = await digitalOcean
        .from("tracks")
        .delete()
        .eq("id", trackId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(["archived-tracks"]);
      toast.success("Track permanently deleted");
    },
    onError: () => {
      toast.error("Failed to delete track");
    },
  });

  if (isLoading || !currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
            <ArchiveIcon className="w-8 h-8" />
            Archived Tracks
          </h1>
          <Button variant="outline" onClick={() => navigate("/MyTracks")} tooltip="Return to your active tracks">
            Back to My Tracks
          </Button>
        </div>

        {!archivedTracks || archivedTracks.length === 0 ? (
          <div className="text-center py-12">
            <ArchiveIcon className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No archived tracks.</p>
            <Button onClick={() => navigate("/MyTracks")} tooltip="View all your active tracks">View Active Tracks</Button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {archivedTracks.map((track) => (
              <div key={track.id} className="bg-card p-4 rounded-lg border border-border">
                <img
                  src={track.cover_image_url || getDefaultCoverImage(track.id)}
                  alt={track.title}
                  className="w-full aspect-square object-cover rounded mb-4"
                />
                
                <h3 className="text-xl font-semibold text-card-foreground mb-1 truncate">
                  {track.title}
                </h3>
                <p className="text-muted-foreground mb-2">{track.artist_name}</p>
                
                <div className="flex gap-4 text-sm mb-4">
                  <span>❤️ {track.praise_count}</span>
                  <span>👂 {track.total_listens}</span>
                  <span>💬 {track.scathe_count}</span>
                </div>

                <p className="text-xs text-muted-foreground mb-4">
                  Archived on {new Date(track.updated_at).toLocaleDateString()}
                </p>

                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={() => restoreMutation.mutate(track.id)}
                    tooltip="Move this track back to your active tracks"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Restore
                  </Button>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      if (confirm("Permanently delete this track? This cannot be undone.")) {
                        deleteMutation.mutate(track.id);
                      }
                    }}
                    tooltip="Permanently delete this track - cannot be undone"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
